import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AiProfileInfo } from '@/customTypes/chapter';
import { CloseIconStoryGame } from 'public/assets';
import useTranslation from 'next-translate/useTranslation';

interface MobileStoryGameCharacterNameInputProps {
  onPrevious?: () => void;
  onNext?: () => void;
  firstName: string;
  onChangeAiProfile: (profile: AiProfileInfo) => void;
  lastName: string;
  characterImage?: string | File;
  onProfileEdit: () => void;
  placeholderLastName: string;
  placeholderFirstName: string;
  onClose: () => void;
  displayName: string;
  isError?: boolean;
}

const MobileStoryGameCharacterNameInput: React.FC<
  MobileStoryGameCharacterNameInputProps
> = ({
  firstName,
  onChangeAiProfile,
  lastName,
  characterImage,
  onProfileEdit,
  placeholderLastName,
  placeholderFirstName,
  onClose,
  displayName,
  isError,
}) => {
  const { t } = useTranslation();
  const [name1, setName1] = useState(lastName);
  const [name2, setName2] = useState(firstName);
  useEffect(() => {
    setName1(lastName);
    setName2(firstName);
  }, [firstName, lastName]);

  // const handleInputChange = (
  //   e: React.ChangeEvent<HTMLInputElement>,
  //   onChange: (value: string) => void,
  //   key: 'firstName' | 'lastName',
  // ) => {
  //   const value = e.target.value;
  //   onChange(value);
  //   // firstName, lastName의 value가 둘 다 있을 떄 isError = false
  //   const isErrorValue =
  //     isError && (key === 'lastName' ? !value || !name2 : !name1 || !value);
  //   console.log('isErrorValue' + key, isErrorValue, value, name2);
  //   onChangeAiProfile({ [key]: value, isError: isErrorValue });
  //   // isError && e.target.classList.toggle('empty', value === '');
  // };

  const onSubmit = () => {
    if (name1 === '' || name2 === '') {
      return;
    } else {
      onChangeAiProfile({ lastName: name1, firstName: name2, isError: false });
      onClose();
    }
  };

  return (
    <div css={styles.bg}>
      <div css={styles.container}>
        <div css={styles.header}>
          <button className="close-button" onClick={onClose}>
            <CloseIconStoryGame />
          </button>
        </div>
        <div css={styles.innerContainer}>
          <div css={styles.contentContainer}>
            <h2 css={styles.title}>
              {t('storygame_ai_onboarding_popup_title_what_name', {
                value: displayName,
              })}
            </h2>
            <div css={styles.inputContainer}>
              <input
                type="text"
                value={name1}
                onChange={(e) => setName1(e.target.value)}
                placeholder={placeholderLastName}
                css={styles.input}
                className={isError && name1 === '' ? 'empty' : ''}
                inputMode="text"
              />
              <input
                type="text"
                value={name2}
                onChange={(e) => setName2(e.target.value)}
                placeholder={placeholderFirstName}
                css={styles.input}
                className={isError && name2 === '' ? 'empty' : ''}
                inputMode="text"
              />
            </div>
          </div>

          <button
            css={
              name1 === '' || name2 === ''
                ? disabledButtonStyle
                : startButtonStyle
            }
            onClick={onSubmit}
            disabled={name1 === '' || name2 === ''}
          >
            {t('storygame_ai_onboarding_screen_button_change')}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  bg: css`
    position: absolute;
    width: 100%;
    height: 100vh;
    background: var(--blackA25, rgba(0, 0, 0, 0.25));
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
  `,
  container: css`
    max-width: 400px;
    width: calc(100% - 32px);
    height: 225px;
    border-radius: var(--radius-ms, 12px);
    border: 1px solid var(--whiteA25, rgba(255, 255, 255, 0.25));
    background: var(--blackA75, rgba(0, 0, 0, 0.75));
    backdrop-filter: blur(12px);
    display: flex;
    flex-direction: column;
  `,
  innerContainer: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0px 24px 24px 24px;
  `,

  contentContainer: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
  `,
  title: css`
    color: var(--whiteA90, rgba(255, 255, 255, 0.9));
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px; /* 144.444% */

    margin-bottom: 8px;
  `,
  inputContainer: css`
    display: flex;
    gap: 10px;
    width: 100%;
  `,
  input: css`
    flex: 1 1 0;
    width: 0; // 이 속성이 중요합니다
    padding: 10px;
    border: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.25);
    background-color: transparent;
    color: white;
    font-size: 16px;
    text-align: center;
    transition: border-bottom-color 0.3s ease;
    border-radius: 0;
    -webkit-appearance: none;
    appearance: none;

    &::placeholder {
      color: #ffffff80;
    }

    &:focus {
      outline: none;
      border-bottom-color: var(--purpleHighlight, #9457eb);
    }

    &.empty:not(:focus) {
      border-bottom-color: var(--red, #ff3a58);
    }
  `,
  header: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;

    padding: 12px;

    .close-button {
      width: 1.5rem;
      height: 1.5rem;
      cursor: pointer;
    }
  `,
};

const buttonBaseStyle = css`
  display: flex;

  justify-content: center;
  align-items: center;
  padding: 12px 0;

  border-radius: var(--radius-sm, 6px);
  border: 1px solid var(--whiteA25, rgba(255, 255, 255, 0.25));

  cursor: pointer;

  text-align: center;
  font-size: 16px;
  line-height: 24px; /* 150% */

  width: 100%;
  height: 3rem;
`;

const startButtonStyle = css`
  ${buttonBaseStyle}
  background-color: #753BBD;
  color: white;
  border: none;
`;

const disabledButtonStyle = css`
  ${buttonBaseStyle}
  border:none;
  background: var(--whiteA10, rgba(255, 255, 255, 0.1));
  color: rgba(255, 255, 255, 0.25);
`;

export default MobileStoryGameCharacterNameInput;
